import { useEffect, useMemo, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'

import { Loader } from 'components/Loader/Loader'
import UserCardContent from 'components/UI/UserCard/UserCardContent'
import {
  FeedAnalyticsCardContent,
  FeedAnalyticsCardDropdown
} from 'components-ref/organisms/analytics/content'
import { getContentPremiumAnalytics } from 'services/endpoints/analytic'
import { useAnalyticsContentCacheStore } from 'store/analyticsContentCacheStore'
import { type ContentFilter } from 'types/api/analytic'
import { type ContentModalSortFilter } from 'types/interfaces/IContentModalSortFilter'

interface PremiumAnalyticsProps {
  selectedCategory?: string
  filter?: ContentFilter
  sort: ContentModalSortFilter
}

export function PremiumAnalytics({ selectedCategory, filter, sort }: PremiumAnalyticsProps) {
  const { ref, inView } = useInView({})
  const { hydrated, getCache, setCache } = useAnalyticsContentCacheStore()

  const [ready, setReady] = useState(false)
  const [fallbackData, setFallbackData] = useState<any[] | null>(null)
  const [enabled, setEnabled] = useState(false)

  const normalizedCategoryId = useMemo(() => {
    return selectedCategory ?? undefined
  }, [selectedCategory])

  const cacheKey = useMemo(() => {
    return `premium:${normalizedCategoryId ?? 'all'}:${JSON.stringify(filter)}:${JSON.stringify(sort)}`
  }, [normalizedCategoryId, filter, sort])

  const queryKey = useMemo(
    () => [
      'contentAnalytics',
      'premium',
      normalizedCategoryId ?? 'all',
      sort,
      JSON.stringify(filter)
    ],
    [normalizedCategoryId, sort, filter]
  )

  // Hydrate from Zustand cache (render immediately if present)
  useEffect(() => {
    if (!hydrated) return
    const cached = getCache(cacheKey)
    if (cached?.data?.length) {
      setFallbackData(cached.data)
    }
    setEnabled(true) // allow network query
    setReady(true)
  }, [hydrated, getCache, cacheKey])

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetching,
    hasNextPage
  } = useInfiniteQuery<any>(
    queryKey,
    async ({ pageParam = 1 }) => {
      return await getContentPremiumAnalytics({
        categoryId: normalizedCategoryId,
        filter,
        pageParam,
        sort
      })
    },
    {
      getNextPageParam: lastPage => lastPage?.nextCursor,
      enabled,
      staleTime: 60_000,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  )

  // Save fresh data to Zustand cache
  useEffect(() => {
    if (data && !isFetching) {
      const all = data?.pages?.flatMap?.(p => (Array.isArray(p?.data) ? p.data : [])) ?? []
      setCache(cacheKey, { data: all, lastFetched: Date.now() })
      setFallbackData(all)
    }
  }, [data, isFetching, cacheKey, setCache])

  // Pagination observer
  useEffect(() => {
    if (inView && hasNextPage && !isFetching && !isLoading) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetching, isLoading, fetchNextPage])

  if (!ready) return null

  const hasCached = !!fallbackData?.length
  const isFirstLoadWithoutCache = !hasCached && isLoading

  if (isFirstLoadWithoutCache) return <Loader />

  const allCards =
    data?.pages?.flatMap?.(p => (Array.isArray(p?.data) ? p.data : [])) ?? fallbackData ?? []

  return (
    <>
      {allCards.map((card: any, index: number) => (
        <span
          ref={hasNextPage && index === allCards.length - 1 ? ref : null}
          key={index}
        >
          <UserCardContent
            thumb_src={card.photos[0]?.url ?? card.videos[0]?.active_thumb ?? ''}
            cardContent={<FeedAnalyticsCardContent card={card} sort={sort} index={index} />}
            hasDropDown={true}
            dropDownContent={<FeedAnalyticsCardDropdown card={card} />}
            customIconClass='content-icon-square'
            iconSize='80'
            card={card}
            index={index}
          />
        </span>
      ))}
    </>
  )
}